import { browserTracingIntegration, ErrorBoundary, init as initSentry } from '@sentry/react';

initSentry({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'local',
    sendDefaultPii: true,
    // Replay + profiling are added lazily (see ./lib/lazy-sentry-extras).
    integrations: [browserTracingIntegration()],

    // Traces: 100% on dev, 20% on production
    tracesSampleRate: import.meta.env.VITE_SENTRY_ENVIRONMENT === 'production' ? 0.2 : 1.0,

    // Profiling: 100% of traces on dev, 20% on production
    profilesSampleRate: import.meta.env.VITE_SENTRY_ENVIRONMENT === 'production' ? 0.2 : 1.0,

    // Session Replay: 100% on dev, 10% on production (100% on error always)
    replaysSessionSampleRate: import.meta.env.VITE_SENTRY_ENVIRONMENT === 'production' ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,
});

// Defer Replay + Profiling integration loading until the browser is idle so
// their ~150-250 KB ships in a separate chunk that doesn't block first paint.
if (typeof window !== 'undefined') {
    const schedule = (cb: () => void) => {
        const ric = (window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number }).requestIdleCallback;
        if (ric) ric(cb, { timeout: 5000 });
        else setTimeout(cb, 2000);
    };
    schedule(() => {
        import('./lib/lazy-sentry-extras').then((m) => m.loadSentryExtras()).catch(() => {});
    });
}

import '@silevis/reactgrid/styles.css';
import '../css/app.css';
import '../css/reactgrid-overrides.css';

import { createInertiaApp, router } from '@inertiajs/react';
import axios from 'axios';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import type { ComponentType } from 'react';
import { createRoot } from 'react-dom/client';
import { initializeTheme } from './hooks/use-appearance';
import { bootAmplitude, prepareAmplitudeNavigation, type AmplitudePageProps } from './lib/amplitude';

axios.defaults.withCredentials = true;
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

createInertiaApp({
    // Only append brand suffix when the page title doesn't already include
    // 'LuxRally' — most public pages already format their MetaHead title as
    // 'X | LuxRally Travel', so unconditional appending produced
    // 'X | LuxRally Travel - LuxRally Travel Web' in browser tabs.
    title: (title) => (title?.includes('LuxRally') ? title : `${title} | LuxRally Travel`),
    resolve: (name) =>
        resolvePageComponent<{ default: ComponentType }>(
            `./pages/${name}.tsx`,
            import.meta.glob<{ default: ComponentType }>('./pages/**/*.tsx'),
        ) as unknown as Promise<ComponentType>,
    setup({ el, App, props }) {
        const root = createRoot(el);

        // Amplitude: boot once (consent-gated), then follow the CMS session
        // across Inertia visits so sign-in / sign-out update the user id.
        bootAmplitude(props.initialPage.props as AmplitudePageProps);
        router.on('beforeUpdate', (event) => prepareAmplitudeNavigation(event.detail.page.url, event.detail.page.props as AmplitudePageProps));
        router.on('navigate', (event) => bootAmplitude(event.detail.page.props as AmplitudePageProps));

        root.render(
            <ErrorBoundary
                fallback={({ resetError }) => (
                    <div className="flex min-h-screen items-center justify-center bg-background p-6">
                        <div className="w-full max-w-md rounded-lg border border-border bg-card p-8 text-center shadow-sm">
                            <p className="text-lg font-medium text-foreground">Something went wrong</p>
                            <p className="text-cms-body mt-2 text-muted-foreground">
                                An unexpected error occurred. This has been reported automatically.
                            </p>
                            <div className="mt-6 flex justify-center gap-3">
                                <button
                                    onClick={resetError}
                                    className="text-cms-body rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                                >
                                    Try again
                                </button>
                                <button
                                    onClick={() => window.location.reload()}
                                    className="text-cms-body rounded-md border border-border px-4 py-2 font-medium text-muted-foreground transition-colors hover:bg-accent"
                                >
                                    Reload page
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            >
                <App {...props} />
            </ErrorBoundary>,
        );
    },
    progress: {
        // eslint-disable-next-line no-restricted-syntax -- Inertia progress-bar init option: injected into nprogress's own <style> on every page, public site included, where the --cms-* tokens are not the chrome
        color: '#4B5563',
    },
});

// Twind is loaded lazily on demand by the page-builder blocks that render
// CMS-authored HTML (see resources/js/lib/lazy-twind.ts). Pages without those
// blocks pay zero — the previous eager setup() shipped ~150 KB on every page.

// This will set light / dark mode on load...
initializeTheme();
